import type { Node } from "../../../../graph/types.ts";
import { arnInfo } from "../../../../integrations/resources/aws/util.ts";
import type { AssessmentNodes } from "../../../../types/assessment/data/index.ts";
import { Monitor } from "../../../../types/assessment/monitor.ts";

const accountFixGenerator = (status: "disabled" | "enabled") => ({
  gcloud: {
    description: `${
      status === "disabled" ? "Re-enable" : "Disable"
    } these service accounts by running these commands in Google Cloud Shell.`,
    type: "shell" as const,
    generate: (node: Node<AssessmentNodes, "identity">) =>
      `gcloud iam service-accounts ${
        status === "disabled" ? "enable" : "disable"
      } '${node.key}'`,
  },
  aws: {
    description: `${
      status === "disabled" ? "Re-enable" : "Disable"
    } these IAM roles by running these commands in AWS Cloud Shell.`,
    type: "shell" as const,
    generate: (node: Node<AssessmentNodes, "identity">) => {
      const awsPartition = arnInfo(node.key).partition;
      return `aws iam ${
        status === "disabled" ? "detach-role-policy" : "attach-role-policy"
      } --role-name ${
        node.data.label
      } --policy-arn arn:${awsPartition}:iam::aws:policy/AWSDenyAll`;
    },
  },
});

export const IdentityPresets = {
  "Unused User Accounts": Monitor({
    show: "identity",
    // TODO: ENG-5452 - Add Entra once we have data
    scopes: ["aws", "azure-ad", "gcloud", "k8s", "okta", "workspace", "custom"],
    searchTerm:
      'identity={type:"user" external:!"true"}<-credential:last90:"unused" ^credential:last90:"used"',
    label: "Unused user account",
    priority: "HIGH",
    cta: "This user account has not been used in the last 90 days. Consider removing this user from your directory.",
    remediation: (action: string) =>
      `This user has not been used in the last 90 days. P0 has automatically ${action} to remove this user.`,
    description:
      "User accounts with no recorded activity in the last 90 days, including login or access via other credentials",
  }),
  // TODO: ENG-5452 - Add Entra once we have data
  "Unused Service Accounts": Monitor({
    scopes: ["gcloud", "custom"],
    show: "identity",
    searchTerm:
      'identity={status:"active" type:"service-account"}<-credential:last40:"unused" ^credential:last40:"used"',
    label: "Unused service account",
    priority: "HIGH",
    cta: "This service account has not performed any actions within the last 40 days. Consider disabling this service account.",
    remediation: (action: string) =>
      `This service account has not performed any actions within the last 40 days. P0 has automatically ${action} to disable this service account.`,
    description: "Service accounts that have been inactive for 40 days.",
    fix: accountFixGenerator("enabled"),
    revert: accountFixGenerator("disabled"),
  }),
  // TODO: ENG-5400 - Add Entra once we have risks for it
  "Privileged Group Open to Public": Monitor({
    scopes: ["aws", "gcloud"], // TODO: directory-scoped
    show: "identity",
    searchTerm: 'identity={type:"group" accessJoin:"public"}->risk:CRITICAL',
    label: "Privileged group open to public",
    priority: "CRITICAL",
    cta:
      "This group has privileged access to your project, but its membership is open to the public. " +
      "Anyone on the Internet can join this group and gain access to your resources. " +
      "Restrict join access to this group.",
    description:
      "This group has privileged access, but anyone on the Internet can join it",
  }),
  "Privileged Group Open to Organization": Monitor({
    scopes: ["aws", "gcloud"], // TODO: directory-scoped
    show: "identity",
    searchTerm: 'identity={type:"group" accessJoin:"domain"}->risk:CRITICAL',
    label: "Privileged group open to organization",
    priority: "CRITICAL",
    cta:
      "This group has privileged access to your organization, but its membership is open to anyone in your organization. " +
      "Any member of your organization can join this group and gain access to your resources. " +
      "Restrict join access to this group.",
    description:
      "This group has privileged access, but anyone in your organization can join it",
  }),
  "Unused IAM Roles": Monitor({
    scopes: ["aws"],
    show: "identity",
    searchTerm:
      'identity={type:"aws-iam-role" status:"active"}<-credential:last90:"unused" ^credential:last90:"used"',
    label: "Unused IAM roles",
    priority: "HIGH",
    cta: "This role has not been accessed in the last 90 days. Consider removing this role.",
    remediation: (action: string) =>
      `This role has not been accessed in the last 90 days. P0 has automatically ${action} to remove this role.`,
    description: "Roles that have not been used in the last 90 days.",
    fix: accountFixGenerator("enabled"),
    revert: accountFixGenerator("disabled"),
  }),
  "Disabled MFA": Monitor({
    scopes: ["aws", "gcloud", "k8s", "workspace", "azure-ad"],
    show: "identity",
    searchTerm: 'identity=mfa:"disabled"',
    label: "Disabled multi-factor authentication",
    priority: "HIGH",
    cta: "This user does not have multi-factor authentication enabled. Consider enforcing MFA for this user.",
    description: "Users with disabled multi-factor authentication.",
  }),
  "Disabled MFA for Root User": Monitor({
    scopes: ["aws"],
    show: "identity",
    searchTerm: 'identity={mfa:"disabled" label:"root"}',
    label: "Disabled multi-factor authentication for Root User",
    priority: "CRITICAL",
    cta: "Root User does not have multi-factor authentication enabled. Strongly advised to enforce MFA for this user.",
    description: "Root User has disabled multi-factor authentication.",
  }),
  "Account Principal in Trust Policy": Monitor({
    scopes: ["aws"],
    show: "identity",
    searchTerm:
      'identity=type:"aws-iam-role" identity=trustedAccountPrincipals:',
    label: "Account principal in trust policy",
    priority: "HIGH",
    cta: "This role has an account principal in its trust policy, which may grant access to more users than intended. Consider specifying a more specific principal to limit who has access.",
    description: "Roles with account principals in their trust policies.",
  }),
};
